/**
 * Turns the character-creation wizard's choices into finished numbers.
 * Shared with the server so a submitted draft is recomputed, never trusted.
 */
import { ABILITIES, type Ability, type ClassKey, type ProficiencyLevel, type SkillKey } from './constants'
import { abilityModifier, proficiencyBonus } from './calc'
import { CLASS_BUILDS_BY_ID, type ClassBuild } from './data/classes'
import { SPECIES_BY_ID, type SpeciesDef } from './data/species'
import { BACKGROUNDS_BY_ID, type BackgroundDef } from './data/backgrounds'
import { FEATS_BY_ID } from './data/feats'
import { ARMOR_BY_ID, PACKS, WEAPONS_BY_ID } from './data/equipment'
import { SPELLS_BY_ID } from './data/spells'

export type AbilityMethod = 'standard' | 'pointbuy' | 'roll' | 'manual'

/**
 * A granted feat plus whatever it makes you choose. Magic Initiate hands out
 * spells, Skilled hands out proficiencies, and Crafter/Musician just need a
 * note of which tools you picked.
 */
export interface FeatSelection {
  featId: string
  cantrips: string[]
  spell: string
  skills: SkillKey[]
  notes: string
}

export const EMPTY_FEAT_SELECTION: FeatSelection = {
  featId: '', cantrips: [], spell: '', skills: [], notes: '',
}

/** Which class list a Magic Initiate feat draws from, if any. */
export function magicInitiateList(featId: string): ClassKey | null {
  if (featId === 'magic-initiate-cleric') return 'cleric'
  if (featId === 'magic-initiate-druid') return 'druid'
  if (featId === 'magic-initiate-wizard') return 'wizard'
  return null
}

export function magicInitiateAbility(featId: string): Ability {
  return featId === 'magic-initiate-wizard' ? 'int' : 'wis'
}

export interface CharacterDraft {
  name: string
  classId: ClassKey | ''
  speciesId: string
  /** Sub-choices keyed by SpeciesChoice.id, e.g. { elvenLineage: 'wood' }. */
  speciesChoices: Record<string, string>
  backgroundId: string

  abilityMethod: AbilityMethod
  /** Before the background's increases are applied. */
  baseScores: Record<Ability, number>
  /** The six numbers produced by rolling, which must each be placed once. */
  rolledPool: number[]
  /** From the background: either +2/+1 or +1/+1/+1. */
  backgroundIncreases: Partial<Record<Ability, number>>

  classSkills: SkillKey[]
  speciesSkills: SkillKey[]
  expertise: SkillKey[]
  /** Keyed by ClassChoice.id; always an array even for single picks. */
  classChoices: Record<string, string[]>
  /** The Origin feat from your background, plus its follow-up picks. */
  backgroundFeat: FeatSelection
  /** Humans get a second Origin feat. Null for everyone else. */
  speciesFeat: FeatSelection | null
  /** Resolved tool picks, keyed by the prompt they answer. */
  toolChoices: Record<string, string>

  equipmentOptionId: string
  cantrips: string[]
  spells: string[]
  languages: string[]

  alignment: string
  appearance: string
  personality: string
  backstory: string
  /** The Witchlight hook: what the Hourglass Coven took from you. */
  lostSomething: string
}

export const EMPTY_DRAFT: CharacterDraft = {
  name: '',
  classId: '',
  speciesId: '',
  speciesChoices: {},
  backgroundId: '',
  abilityMethod: 'standard',
  baseScores: { str: 10, dex: 10, con: 10, int: 10, wis: 10, cha: 10 },
  rolledPool: [],
  backgroundIncreases: {},
  classSkills: [],
  speciesSkills: [],
  expertise: [],
  classChoices: {},
  backgroundFeat: { ...EMPTY_FEAT_SELECTION },
  speciesFeat: null,
  toolChoices: {},
  equipmentOptionId: '',
  cantrips: [],
  spells: [],
  languages: [],
  alignment: '',
  appearance: '',
  personality: '',
  backstory: '',
  lostSomething: '',
}

// ---------- Ability score methods ----------

export const STANDARD_ARRAY = [15, 14, 13, 12, 10, 8] as const

/** 2024 point buy: 27 points, scores 8-15 before background increases. */
export const POINT_BUY_COST: Record<number, number> = {
  8: 0, 9: 1, 10: 2, 11: 3, 12: 4, 13: 5, 14: 7, 15: 9,
}
export const POINT_BUY_BUDGET = 27

export function pointBuySpent(scores: Record<Ability, number>): number {
  return ABILITIES.reduce((total, ability) => total + (POINT_BUY_COST[scores[ability]] ?? 0), 0)
}

/** One ability score the traditional way: roll 4d6 and drop the lowest die. */
export function rollAbilityScore(): { total: number; dice: number[]; dropped: number } {
  const dice = Array.from({ length: 4 }, () => 1 + Math.floor(Math.random() * 6))
  const sorted = [...dice].sort((a, b) => a - b)
  const dropped = sorted[0]!
  return { total: sorted.slice(1).reduce((a, b) => a + b, 0), dice, dropped }
}

export const rollAbilityPool = (): number[] =>
  Array.from({ length: 6 }, () => rollAbilityScore().total).sort((a, b) => b - a)

/**
 * Multiset difference between a fixed pool of numbers and what has been placed,
 * so a pool containing duplicates (which rolled scores often do) still checks out.
 */
export function poolLeftovers(
  pool: readonly number[],
  assigned: readonly number[],
): { missing: number[]; extra: number[] } {
  const remaining = [...pool]
  const extra: number[] = []

  for (const value of assigned) {
    const index = remaining.indexOf(value)
    if (index === -1) extra.push(value)
    else remaining.splice(index, 1)
  }
  return { missing: remaining, extra }
}

// ---------- Assembled character ----------

export interface Attack {
  name: string
  bonus: string
  damage: string
  notes: string
}

export interface BuiltCharacter {
  scores: Record<Ability, number>
  modifiers: Record<Ability, number>
  proficiencyBonus: number
  hpMax: number
  hitDie: number
  armorClass: number
  acSource: string
  initiativeBonus: number
  speed: number
  otherSpeeds: Record<string, number>
  size: string
  saveProficiencies: Ability[]
  skillProficiencies: Partial<Record<SkillKey, ProficiencyLevel>>
  attacks: Attack[]
  equipment: string[]
  coins: { cp: number; sp: number; ep: number; gp: number; pp: number }
  classFeatures: { name: string; text: string }[]
  speciesTraits: { name: string; text: string }[]
  feats: { name: string; text: string }[]
  languages: string[]
  armorTraining: { light: boolean; medium: boolean; heavy: boolean; shields: boolean }
  weaponTraining: string
  toolTraining: string[]
  spellcasting: {
    ability: Ability
    modifier: number
    saveDC: number
    attackBonus: number
    cantrips: string[]
    spells: string[]
  } | null
  /** Spells granted by Magic Initiate feats, which any class can have. */
  featSpells: {
    featName: string
    ability: Ability
    saveDC: number
    attackBonus: number
    cantrips: string[]
    spell: string
  }[]
}

export interface BuildContext {
  classBuild: ClassBuild
  species: SpeciesDef
  background: BackgroundDef
}

export function resolveContext(draft: CharacterDraft): BuildContext | null {
  const classBuild = draft.classId ? CLASS_BUILDS_BY_ID.get(draft.classId) : undefined
  const species = SPECIES_BY_ID.get(draft.speciesId)
  const background = BACKGROUNDS_BY_ID.get(draft.backgroundId)
  if (!classBuild || !species || !background) return null
  return { classBuild, species, background }
}

export function finalScores(draft: CharacterDraft): Record<Ability, number> {
  const result = { ...draft.baseScores }
  for (const ability of ABILITIES) {
    result[ability] = Math.min(20, result[ability] + (draft.backgroundIncreases[ability] ?? 0))
  }
  return result
}

/** Level 1 only — the wizard builds starting characters. */
export function buildCharacter(draft: CharacterDraft): BuiltCharacter | null {
  const ctx = resolveContext(draft)
  if (!ctx) return null
  const { classBuild, species, background } = ctx

  const scores = finalScores(draft)
  const modifiers = Object.fromEntries(
    ABILITIES.map((a) => [a, abilityModifier(scores[a])]),
  ) as Record<Ability, number>
  const pb = proficiencyBonus(1)

  const selections = featSelections(draft)
  const featIds = selections.map((s) => s.featId)
  const toughBonus = featIds.includes('tough') ? 2 : 0
  const hpMax = classBuild.hitDie + modifiers.con + (species.hpPerLevel ?? 0) + toughBonus

  // ---- Skills ----
  const skillProficiencies: Partial<Record<SkillKey, ProficiencyLevel>> = {}
  for (const skill of background.skills) skillProficiencies[skill] = 'proficient'
  for (const skill of species.skillProficiencies ?? []) skillProficiencies[skill as SkillKey] = 'proficient'
  for (const skill of draft.speciesSkills) skillProficiencies[skill] = 'proficient'
  for (const skill of draft.classSkills) skillProficiencies[skill] = 'proficient'
  for (const selection of featSelections(draft)) {
    for (const skill of selection.skills) skillProficiencies[skill] = 'proficient'
  }
  for (const skill of draft.expertise) {
    if (skillProficiencies[skill]) skillProficiencies[skill] = 'expertise'
  }

  // ---- Equipment, armour, and AC ----
  const option = classBuild.equipmentOptions.find((o) => o.id === draft.equipmentOptionId)
    ?? classBuild.equipmentOptions[0]!

  const equipment: string[] = [...option.items]
  for (const pack of option.packs ?? []) {
    equipment.push(`${pack}:`, ...(PACKS[pack] ?? []).map((entry) => `  ${entry}`))
  }

  const armor = option.armorId ? ARMOR_BY_ID.get(option.armorId) : undefined
  const shieldBonus = option.shield ? 2 : 0

  let armorClass: number
  let acSource: string

  if (armor) {
    const dexApplied = armor.dexCap === null ? modifiers.dex : Math.min(modifiers.dex, armor.dexCap)
    armorClass = armor.baseAc + dexApplied + shieldBonus
    acSource = `${armor.name}${option.shield ? ' + Shield' : ''}`
  } else if (classBuild.id === 'barbarian') {
    armorClass = 10 + modifiers.dex + modifiers.con + shieldBonus
    acSource = 'Unarmored Defense (Dex + Con)'
  } else if (classBuild.id === 'monk') {
    armorClass = 10 + modifiers.dex + modifiers.wis
    acSource = 'Unarmored Defense (Dex + Wis)'
  } else {
    armorClass = 10 + modifiers.dex + shieldBonus
    acSource = 'Unarmored'
  }

  // ---- Attacks ----
  const attacks: Attack[] = []
  const seen = new Set<string>()
  for (const weaponId of option.weaponIds) {
    if (seen.has(weaponId)) continue
    seen.add(weaponId)
    const weapon = WEAPONS_BY_ID.get(weaponId)
    if (!weapon) continue

    // Finesse and ranged weapons use the better of Strength and Dexterity.
    const usesDex = weapon.ranged || weapon.properties.includes('Finesse')
    const ability: Ability = usesDex && modifiers.dex >= modifiers.str ? 'dex' : 'str'
    const mod = modifiers[ability]

    attacks.push({
      name: weapon.name,
      bonus: formatSigned(pb + mod),
      damage: `${weapon.damage}${mod === 0 ? '' : formatSigned(mod)} ${DAMAGE_ABBREV[weapon.damageType]}`,
      notes: [
        weapon.range && `Range ${weapon.range}`,
        weapon.versatile && `Versatile ${weapon.versatile}`,
        ...weapon.properties.filter((p) => p !== 'Thrown' && p !== 'Versatile' && p !== 'Ammunition'),
        classBuild.weaponMastery ? `Mastery: ${weapon.mastery}` : '',
      ].filter(Boolean).join(', '),
    })
  }

  if (classBuild.id === 'monk') {
    attacks.unshift({
      name: 'Unarmed Strike (Martial Arts)',
      bonus: formatSigned(pb + Math.max(modifiers.str, modifiers.dex)),
      damage: `1d6${formatSigned(Math.max(modifiers.str, modifiers.dex))} bludgeoning`,
      notes: 'Bonus Action attack',
    })
  }

  // ---- Spellcasting ----
  let spellcasting: BuiltCharacter['spellcasting'] = null
  if (classBuild.spellcasting) {
    const ability = classBuild.spellcasting.ability
    const mod = modifiers[ability]
    spellcasting = {
      ability,
      modifier: mod,
      saveDC: 8 + pb + mod,
      attackBonus: pb + mod,
      cantrips: draft.cantrips.filter((id) => SPELLS_BY_ID.has(id)),
      spells: draft.spells.filter((id) => SPELLS_BY_ID.has(id)),
    }
  }

  // ---- Magic Initiate spells, which work with or without a caster class ----
  const featSpells: BuiltCharacter['featSpells'] = []
  for (const selection of selections) {
    if (!magicInitiateList(selection.featId)) continue
    const feat = FEATS_BY_ID.get(selection.featId)
    const ability = magicInitiateAbility(selection.featId)
    const mod = modifiers[ability]
    featSpells.push({
      featName: feat?.name ?? selection.featId,
      ability,
      saveDC: 8 + pb + mod,
      attackBonus: pb + mod,
      cantrips: selection.cantrips.filter((id) => SPELLS_BY_ID.has(id)),
      spell: SPELLS_BY_ID.has(selection.spell) ? selection.spell : '',
    })
  }

  // ---- Traits from species choices ----
  const speciesTraits = [...species.traits]
  if (species.choice) {
    const picked = draft.speciesChoices[species.choice.id]
    const option = species.choice.options.find((o) => o.value === picked)
    if (option) {
      speciesTraits.push(...(option.traits ?? [{ name: species.choice.label, text: option.label }]))
    }
  }

  // ---- Class features, including the choices made ----
  const classFeatures = [...classBuild.level1Features]
  for (const choice of classBuild.choices) {
    const picked = draft.classChoices[choice.id] ?? []
    if (!picked.length || choice.id === 'expertise') continue
    const labels = picked
      .map((value) => choice.options.find((o) => o.value === value)?.label ?? value)
      .join(', ')
    classFeatures.push({ name: choice.label, text: labels })
  }

  const toolTraining = [resolveTool(background.tool.label, draft.toolChoices)]
  if (classBuild.toolTraining) {
    toolTraining.push(resolveTool(classBuild.toolTraining, draft.toolChoices))
  }

  const gold = option.gold + background.startingGold

  return {
    scores,
    modifiers,
    proficiencyBonus: pb,
    hpMax,
    hitDie: classBuild.hitDie,
    armorClass,
    acSource,
    initiativeBonus: featIds.includes('alert') || species.id === 'harengon' ? pb : 0,
    speed: species.speed,
    otherSpeeds: species.otherSpeeds ?? {},
    size: species.size,
    saveProficiencies: [...classBuild.saves],
    skillProficiencies,
    attacks,
    equipment,
    coins: { cp: 0, sp: 0, ep: 0, gp: gold, pp: 0 },
    classFeatures,
    speciesTraits,
    feats: selections
      .map((selection) => FEATS_BY_ID.get(selection.featId))
      .filter((f): f is NonNullable<typeof f> => Boolean(f))
      .map((f) => ({ name: f.name, text: f.text })),
    languages: ['Common', ...draft.languages],
    armorTraining: classBuild.armorTraining,
    weaponTraining: classBuild.weaponTraining,
    toolTraining,
    spellcasting,
    featSpells,
  }
}

/** Background feat plus the species feat, when there is one. */
export function featSelections(draft: CharacterDraft): FeatSelection[] {
  const list: FeatSelection[] = []
  if (draft.backgroundFeat.featId) list.push(draft.backgroundFeat)
  if (draft.speciesFeat?.featId) list.push(draft.speciesFeat)
  return list
}

/** Turns "Musical Instrument of your choice" into the instrument they picked. */
export function resolveTool(label: string, choices: Record<string, string>): string {
  const picked = choices[label]
  return picked ? picked : label
}

export const needsToolChoice = (label: string): boolean => /your choice|one type of/i.test(label)

const formatSigned = (value: number): string => (value < 0 ? `${value}` : `+${value}`)

/** The sheet's Damage column is ~75pt wide, so the type is shortened to fit. */
const DAMAGE_ABBREV: Record<string, string> = {
  slashing: 'slashing',
  piercing: 'piercing',
  bludgeoning: 'bludgeon',
}

// ---------- Validation ----------

export interface DraftIssue { step: string; message: string }

/** What is still missing before the character can be created. */
export function validateDraft(draft: CharacterDraft): DraftIssue[] {
  const issues: DraftIssue[] = []
  const push = (step: string, message: string) => issues.push({ step, message })

  if (!draft.name.trim()) push('details', 'Your character needs a name')
  if (!draft.classId) push('class', 'Choose a class')
  if (!draft.speciesId) push('species', 'Choose a species')
  if (!draft.backgroundId) push('background', 'Choose a background')

  const ctx = resolveContext(draft)
  if (!ctx) return issues
  const { classBuild, species, background } = ctx

  if (species.choice && !draft.speciesChoices[species.choice.id]) {
    push('species', `Choose your ${species.choice.label.toLowerCase()}`)
  }

  const increaseTotal = Object.values(draft.backgroundIncreases).reduce((a, b) => a + b, 0)
  if (increaseTotal !== 3) {
    push('abilities', `Assign your ${background.name} ability increases (+2/+1 or +1/+1/+1)`)
  }

  const assigned = ABILITIES.map((a) => draft.baseScores[a])

  switch (draft.abilityMethod) {
    case 'pointbuy': {
      if (pointBuySpent(draft.baseScores) > POINT_BUY_BUDGET) {
        push('abilities', 'You have spent more than 27 points')
      }
      if (assigned.some((score) => score < 8 || score > 15)) {
        push('abilities', 'Point buy scores must be between 8 and 15')
      }
      break
    }
    case 'standard': {
      const { missing } = poolLeftovers(STANDARD_ARRAY, assigned)
      if (missing.length) {
        push('abilities', `Use each standard array value once — still to place: ${missing.join(', ')}`)
      }
      break
    }
    case 'roll': {
      if (draft.rolledPool.length !== 6) {
        push('abilities', 'Roll your six ability scores')
        break
      }
      const { missing } = poolLeftovers(draft.rolledPool, assigned)
      if (missing.length) {
        push('abilities', `Place every number you rolled — still to place: ${missing.join(', ')}`)
      }
      break
    }
    case 'manual': {
      if (assigned.some((score) => score < 3 || score > 18)) {
        push('abilities', 'Ability scores must be between 3 and 18')
      }
      break
    }
  }

  if (draft.classSkills.length !== classBuild.skillChoices.count) {
    push('skills', `Choose ${classBuild.skillChoices.count} class skills`)
  }

  const speciesSkillsNeeded = species.grantsSkillChoice ?? 0
  if (draft.speciesSkills.length !== speciesSkillsNeeded) {
    push('skills', `Choose ${speciesSkillsNeeded} skill from your species`)
  }
  if (species.skillChoiceFrom) {
    const allowed = species.skillChoiceFrom as string[]
    if (draft.speciesSkills.some((skill) => !allowed.includes(skill))) {
      push('skills', `${species.name}s can only pick from ${allowed.join(', ')}`)
    }
  }

  for (const choice of classBuild.choices) {
    // Expertise picks live in draft.expertise so the Skills step can own them.
    if (choice.id === 'expertise') {
      if (draft.expertise.length !== choice.count) {
        push('skills', `Choose ${choice.count} skills for Expertise`)
      }
      continue
    }
    const picked = draft.classChoices[choice.id] ?? []
    if (picked.length !== choice.count) {
      push('features', `Choose ${choice.count} ${choice.label.toLowerCase()}`)
    }
  }

  if (!draft.equipmentOptionId) push('equipment', 'Pick your starting equipment')

  // Feats that make you choose something else
  if (draft.backgroundFeat.featId !== background.featId) {
    push('features', 'Confirm your background feat')
  }
  if (species.grantsOriginFeat && !draft.speciesFeat?.featId) {
    push('features', `Choose the Origin feat your ${species.name} gets`)
  }

  for (const selection of featSelections(draft)) {
    const feat = FEATS_BY_ID.get(selection.featId)
    const label = feat?.name ?? 'your feat'

    if (magicInitiateList(selection.featId)) {
      if (selection.cantrips.length !== 2) push('features', `Choose 2 cantrips for ${label}`)
      if (!selection.spell) push('features', `Choose a 1st-level spell for ${label}`)
    }
    if (selection.featId === 'skilled' && selection.skills.length !== 3) {
      push('features', `Choose 3 proficiencies for ${label}`)
    }
  }

  // Tool proficiencies that name a category rather than a specific tool
  for (const label of [background.tool.label, classBuild.toolTraining].filter(Boolean) as string[]) {
    if (needsToolChoice(label) && !draft.toolChoices[label]) {
      push('equipment', `Pick your ${label.replace(/ of your choice$/i, '').toLowerCase()}`)
    }
  }

  if (classBuild.spellcasting) {
    const { cantrips, prepared, spellbook } = classBuild.spellcasting
    if (draft.cantrips.length !== cantrips) {
      push('spells', `Choose ${cantrips} cantrip${cantrips === 1 ? '' : 's'}`)
    }
    const spellsNeeded = spellbook ?? prepared
    if (draft.spells.length !== spellsNeeded) {
      push('spells', `Choose ${spellsNeeded} 1st-level spell${spellsNeeded === 1 ? '' : 's'}`)
    }
  }

  return issues
}
